import { useEffect } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiIcon,
  MiMenu,
  MiPlaceholderArea,
  MiSwitch,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import {
  IBidArea,
  ICategoryFilterResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  mdiFloorPlan,
  mdiInformationOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { AddAreaDrawer } from "./components/AddAreaDrawer/AddAreaDrawer";
import { BidArea } from "./components/BidArea/BidArea";
import { MaterialResultCard } from "./components/MaterialResultCard/MaterialResultCard";
import { PriceDetailsDrawer } from "./components/PriceDetailsDrawer/PriceDetailsDrawer";
import { CategoryFilterDrawer } from "../../../components/FilterDrawer/CategoryFilterDrawer";
import { BidDocumentViewer } from "../BidDocumentViewer/BidDocumentViewer";
import { useCreateBidContext } from "../CreateBidContext";
import { ReplaceItemsModal } from "../ReplaceItemsModal/ReplaceItemsModal";
import { AREA_COLUMNS, BID_STATUS } from "../constants";
import { useConfigureBidViewModel } from "./useConfigureBidViewModel";

export function ConfigureBid() {
  const {
    openAreaDrawer,
    selectedFloor,
    addedFloorIds,
    addedFloors,
    groupedAreas,
    planAreas,
    builderId,
    replaceModal,
    showCategoryFilter,
    selectedCategory,
    selectedAttributeValues,
    search,
    materialResults,
    openMaterialResults,
    selectedItem,
    isSuppliedByBuilder,
    checkedAreas,
    planAreaMaterials,
    hasMissingCategories,
    planAreaMaterialIds,
    planAreaMaterialIdsKey,
    debouncedSearch,
    categoryFilterOptions,
    linkedMaterialDetails,
    showLinkedItems,
    showPriceDetails,
    maxFloorId,
    isAddFloorDisabled,
    setShowLinkedItems,
    setShowPriceDetails,
    setSearch,
    setOpenAreaDrawer,
    setSelectedFloor,
    setPreviewAreas,
    setOpenMaterialResults,
    setIsSuppliedByBuilder,
    setSelectedItem,
    getAreaType,
    getMaterialOptions,
    getPlanAreaMaterial,
    getLinkedMaterials,
    isPreviewArea,
    handleAddFloor,
    handleRemoveFloor,
    handleOpenCategoryFilter,
    handleCloseCategoryFilter,
    handleCategoryClick,
    handleAttributeValueClick,
    handleCheckArea,
    handleCancelItem,
    handleSelectItem,
    handleAddItemToAreas,
    handleResetEverything,
    handleOpenReplaceItemModal,
    handleCloseReplaceItemModal,
    handleReplaceItem,
    resetMaterialDrawerStates,
  } = useConfigureBidViewModel();

  const { classes, theme, cx } = useStyles();
  const {
    setIsConfigStepValid,
    openDocumentViewer,
    setOpenDocumentViewer,
    isLoading,
    form,
  } = useCreateBidContext();
  const { watch } = form;
  const { planStatus } = watch();
  const isBidApproved = planStatus === BID_STATUS.APPROVED;

  useEffect(() => {
    if (planAreaMaterialIds.length > 0) {
      getPlanAreaMaterial();
    }
  }, [planAreaMaterialIdsKey]);

  useEffect(() => {
    if (showLinkedItems && planAreaMaterials.length > 0) {
      getLinkedMaterials(planAreaMaterials);
    }
  }, [showLinkedItems, planAreaMaterials]);

  useEffect(() => {
    if (
      showCategoryFilter &&
      (debouncedSearch || selectedCategory?.categoryId)
    ) {
      getMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    setIsConfigStepValid(!hasMissingCategories);
  }, [hasMissingCategories]);

  useEffect(() => {
    if (openDocumentViewer) {
      // close&reset open drawers and selected states when opening the document viewer
      handleResetEverything();
    }
  }, [openDocumentViewer]);

  useEffect(() => {
    if (showPriceDetails) {
      // close&reset open drawers and selected states when opening price details drawer
      handleResetEverything();
      setOpenDocumentViewer(false);
    }
  }, [showPriceDetails]);

  const drawerOpen =
    openAreaDrawer ||
    showCategoryFilter ||
    openMaterialResults ||
    openDocumentViewer;

  useEffect(() => {
    // close price details drawer when any other drawer/viewer opens
    if (drawerOpen) {
      setShowPriceDetails(false);
    }
  }, [drawerOpen]);

  const renderColumn = (areas: IBidArea[], column: number) => {
    const columnAreas = areas.filter((a) => a.specColumn === column);
    return (
      <Flipper
        flipKey={columnAreas.map((a) => a.id).join("-")}
        className={classes.columnBox}
      >
        {columnAreas.map((area, index) => {
          const isPreview = isPreviewArea(area);
          const areaType = getAreaType(area);

          return (
            <Flipped key={area.id} flipId={area.id}>
              {(flippedProps) => (
                <Box {...flippedProps}>
                  <BidArea
                    area={area}
                    areaType={areaType}
                    planAreaMaterials={planAreaMaterials}
                    isLoading={isLoading}
                    isPreview={isPreview}
                    checkedAreas={checkedAreas}
                    selectedCategory={selectedCategory}
                    openMaterialResults={openMaterialResults}
                    disableUp={index === 0}
                    disableDown={index === columnAreas.length - 1}
                    showCategoryFilter={showCategoryFilter}
                    openAreaDrawer={openAreaDrawer}
                    showLinkedItems={showLinkedItems}
                    linkedMaterialDetails={linkedMaterialDetails}
                    isBidApproved={isBidApproved}
                    handleCheckArea={handleCheckArea}
                    handleOpenReplaceItemModal={handleOpenReplaceItemModal}
                    handleOpenItemDrawer={() => {
                      setOpenDocumentViewer(false);
                      handleOpenCategoryFilter(area);
                    }}
                    handlePlaceholder={(cat) => {
                      const filterCategory = categoryFilterOptions.find(
                        (c: ICategoryFilterResponse) =>
                          c.categoryId === cat.categoryId
                      );
                      setOpenDocumentViewer(false);
                      handleOpenCategoryFilter(area, filterCategory);
                    }}
                  />
                </Box>
              )}
            </Flipped>
          );
        })}
      </Flipper>
    );
  };

  return (
    <Box className={classes.root}>
      <MiBox
        headerTitle="Add Areas, Items and Floors"
        headerAction={
          (planAreas?.length > 0 || addedFloorIds.length > 0) && (
            <Box className={classes.headerActionBox}>
              <MiSwitch
                label="Show Linked Items"
                handleChange={() => setShowLinkedItems((prev) => !prev)}
                value={showLinkedItems}
                className={classes.showLinkedSwitch}
              />
              <MiButton
                title="Price Details"
                variant={BUTTON_VARIANTS.OUTLINED}
                onClick={() => setShowPriceDetails((prev) => !prev)}
                size="small"
                className={classes.priceDetailsBtn}
              />
              <MiButton
                title="Add Floor"
                variant={BUTTON_VARIANTS.CONTAINED}
                onClick={handleAddFloor}
                size="small"
                startIcon={{ path: mdiPlus }}
                disabled={
                  isBidApproved ||
                  isAddFloorDisabled ||
                  openAreaDrawer ||
                  showCategoryFilter
                }
              />
            </Box>
          )
        }
        className={classes.miBox}
        height="auto"
      >
        <Box className={classes.contentBox}>
          <Box className={classes.leftContentBox}>
            {addedFloors.map((floor) => {
              const floorAreas = groupedAreas[floor.id] || [];
              const isFloorSelected =
                openAreaDrawer && selectedFloor?.id === floor.id;

              return (
                <Box
                  key={floor.id}
                  className={cx(
                    classes.floorBox,
                    isFloorSelected && classes.activeBorder
                  )}
                >
                  <Box className={classes.floorHeaderBox}>
                    <Typography fontWeight={theme.fontWeight.bold}>
                      {floor.name}
                    </Typography>
                    <Box className={classes.floorHeaderActionBox}>
                      <MiButton
                        title="Add Area"
                        variant={BUTTON_VARIANTS.OUTLINED}
                        startIcon={{ path: mdiPlus, iconSize: 0.8 }}
                        size="small"
                        onClick={() => {
                          setSelectedFloor(floor);
                          setOpenAreaDrawer(true);
                          setOpenDocumentViewer(false);
                        }}
                        disabled={isBidApproved || showCategoryFilter}
                      />
                      <MiMenu
                        disabled={
                          isBidApproved || openAreaDrawer || showCategoryFilter
                        }
                        data={[
                          {
                            label: "Remove Floor",
                            action: () => handleRemoveFloor(floor),
                            icon: mdiTrashCanOutline,
                            color: theme.palette.red.main,
                          },
                        ]}
                      />
                    </Box>
                  </Box>
                  <Box className={classes.areaBox}>
                    {renderColumn(floorAreas, AREA_COLUMNS.LEFT)}
                    {renderColumn(floorAreas, AREA_COLUMNS.RIGHT)}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <PriceDetailsDrawer
            open={showPriceDetails}
            onClose={() => setShowPriceDetails(false)}
          />
        </Box>
        <MiPlaceholderArea
          open={planAreas?.length === 0 && addedFloorIds.length === 0}
          text="No Floors Added"
          helperText="Add your first floor above to begin setting up your bid"
          iconComponent={
            <MiIcon
              path={mdiFloorPlan}
              color={theme.palette.grey.main}
              size={2}
              className={classes.floorIcon}
            />
          }
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            margin: theme.spacing(1),
          }}
          buttons={[
            {
              title: "Add Floor",
              startIcon: mdiPlus,
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleAddFloor,
              disabled: isBidApproved || isAddFloorDisabled,
              tooltip: !maxFloorId && {
                path: mdiInformationOutline,
                text: "To add a floor, first go to Customers and set up maximum number of floors for current customer.",
              },
            },
          ]}
        />
      </MiBox>
      <BidDocumentViewer />
      <AddAreaDrawer
        open={openAreaDrawer}
        onClose={() => {
          setOpenAreaDrawer(false);
          setPreviewAreas([]);
          setSelectedFloor(null);
        }}
        selectedFloor={selectedFloor}
        setPreviewAreas={setPreviewAreas}
      />
      <SearchResultsContainer
        open={openMaterialResults}
        flex={0.6}
        onClose={() => {
          setSelectedItem(null);
          resetMaterialDrawerStates();
          setOpenMaterialResults(false);
        }}
        alertTitle="Select Item(s) and add to Area(s)."
        alertDescription="After selecting the item, choose the Area(s) and select the Add to Area(s) button."
        storageKey="bidMaterials-alert"
        results={materialResults}
        hasFilters={!!search || !!selectedCategory?.categoryId}
        handleResetFilterSelection={resetMaterialDrawerStates}
        renderResults={() =>
          materialResults.map((material: IMaterialsResponse, index: number) => (
            <MaterialResultCard
              key={material.id}
              index={index}
              material={material}
              isSelected={selectedItem?.id === material.id}
              handleSelectItem={handleSelectItem}
              isSuppliedByBuilder={isSuppliedByBuilder}
              setIsSuppliedByBuilder={setIsSuppliedByBuilder}
              checkedAreas={checkedAreas}
              planAreas={planAreas}
              handleCheckArea={handleCheckArea}
              handleCancelItem={handleCancelItem}
              handleAddItemToAreas={handleAddItemToAreas}
            />
          ))
        }
      />
      <CategoryFilterDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter as any}
        title="Filter Items"
        categoryFilterOptions={categoryFilterOptions}
        search={search}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        setSearch={setSearch}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        handleReset={resetMaterialDrawerStates}
        handleResetSelection={resetMaterialDrawerStates}
      />
      <ReplaceItemsModal
        open={replaceModal.open}
        builderId={builderId}
        selectedItem={replaceModal.item}
        selectedArea={replaceModal.area}
        planAreas={planAreas}
        onClose={handleCloseReplaceItemModal}
        onReplaceItem={handleReplaceItem}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "hidden",
    height: "100%",
  },
  headerActionBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  showLinkedSwitch: {
    display: "none",
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.light,
      opacity: 1,
    },
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.grey.medium,
      opacity: 1,
    },
    "& .MuiFormControlLabel-label": {
      color: theme.palette.common.white,
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.medium,
    },
  },
  priceDetailsBtn: {
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
  },
  miBox: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  contentBox: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    gap: 8,
  },
  leftContentBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
    overflow: "auto",
  },
  floorBox: {
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  activeBorder: {
    border: `2px solid ${theme.palette.primary.accent}`,
  },
  floorHeaderBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1, 2),
    borderRadius: 4,
  },
  floorHeaderActionBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  areaBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  columnBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 400,
    gap: theme.spacing(2),
    "@container (min-width: 1200px)": {
      flexWrap: "nowrap",
    },
    "@container (max-width: 1199px)": {
      flexWrap: "wrap",
    },
  },
  floorIcon: {
    borderRadius: "50%",
    backgroundColor: theme.palette.common.white,
    padding: 8,
  },
}));
